import { qualityZoneByRatio, zoneCellClass } from '@/shared/lib';

/**
 * Возвращает className для ячейки рейтинга/балла на основе процента от максимума.
 *
 * Передаётся в `cellClassName` колонки `TableColumn` — класс набрасывается на сам
 * контейнер ячейки таблицы (с padding), что позволяет красить фон и текст без
 * дополнительной обёртки.
 *
 * Применяется к баллам блоков отчёта — у них своя шкала окраски, не связанная с
 * зонами качества. Итоговый балл отчёта красит {@link getQualityZoneCellClassName}.
 *
 * Правила окраски (текст + фон):
 * - `value >= 0.75 × maxValue` → зелёный
 * - `value <= 0.25 × maxValue` → красный; сюда попадает и `value === 0` при положительном `maxValue`
 * - значение между 25% и 75% → без окраски (дефолтный цвет)
 * - `value` или `maxValue` равно `null`/`undefined` → без окраски
 */
export const getRatingCellClassName = (
  value: number | null | undefined,
  maxValue: number | null | undefined,
): string => {
  if (value == null || maxValue == null || maxValue <= 0) return '';
  const ratio = value / maxValue;
  if (ratio >= 0.75) return zoneCellClass.green;
  if (ratio <= 0.25) return zoneCellClass.red;
  return 'font-medium';
};

/**
 * Возвращает className для ячейки итогового балла экспертного отчёта — по зоне
 * качества, к которой относится балл.
 *
 * Зона определяется долей балла от максимума **своего** отчёта: у каждого отчёта
 * своя шкала, поэтому сравнивать балл с абсолютным порогом нельзя. Зоны приходят
 * с бэкенда только для дашборда, список диалогов их не несёт — поэтому балл
 * относит к зоне сам список, по тем же границам, что и сводка дашборда.
 *
 * `value` или `maxValue` равно `null`/`undefined` → без окраски: доля от
 * неизвестного максимума неопределима, зоны у балла нет.
 */
export const getQualityZoneCellClassName = (
  value: number | null | undefined,
  maxValue: number | null | undefined,
): string => {
  if (value == null || maxValue == null || maxValue <= 0) return '';
  return zoneCellClass[qualityZoneByRatio(value / maxValue)];
};
